import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getReservation,
  adminConfirmReservation,
  cancelReservation,
  getReservedSlots,
} from "./reservationsApi";
import { listPayments } from "../../../features/payments/api";


import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Copy,
  CalendarDays,
  Clock3,
  MapPinned,
  Phone,
  UserRound,
  IndianRupee,
  Hash,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";

/** =========================
 * Helpers
 * ========================= */
const isoYMD = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const fmtCreatedOn = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  const date = dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = dt.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${date} at ${time}`;
};

const fmtDateLong = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtTime12 = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return "\u2014";

  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr);

  if (!Number.isFinite(h) || !Number.isFinite(m)) return "\u2014";

  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const min2 = String(m).padStart(2, "0");

  return `${hour12}:${min2} ${ampm}`;
};

const fmtTimeRange = (s) => {
  if (!s?.start || !s?.end) return "\u2014";

  const start = fmtTime12(s.start);
  const end = fmtTime12(s.end);

  if (start === "\u2014" || end === "\u2014") return "\u2014";

  return `${start} - ${end}`;
};

const chip = (t) => `sa-chip sa-chip--${t}`;

const buildScheduleGroups = (reservationDates = []) => {
  const arr = Array.isArray(reservationDates) ? reservationDates : [];
  const normalized = arr
    .filter((x) => x?.date)
    .map((x) => ({
      ymd: isoYMD(x.date),
      dateLabel: fmtDateLong(x.date),
      slots: Array.isArray(x.timeSlots)
        ? x.timeSlots
          .filter((s) => s?.start && s?.end)
          .map((s) => fmtTimeRange(s))
        : [],
    }))
    .filter((x) => x.ymd);

  normalized.sort((a, b) => new Date(a.ymd) - new Date(b.ymd));

  const map = new Map();
  for (const item of normalized) {
    const prev = map.get(item.ymd);
    if (!prev) map.set(item.ymd, { ...item });
    else map.set(item.ymd, { ...prev, slots: [...prev.slots, ...item.slots] });
  }

  return Array.from(map.values()).map((d) => ({
    ...d,
    slots: Array.from(new Set(d.slots)),
  }));
};

export default function ReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // ✅ Cancel modal (same behavior as ReservationsList)
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSaving, setCancelSaving] = useState(false);


  const [taken, setTaken] = useState([]);
  const [checking, setChecking] = useState(false);
  const [paymentRow, setPaymentRow] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  // ---- ctx (venue admin detection) ----
  const ctx = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("va_ctx") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isVenueAdmin = String(ctx.role || "").toLowerCase() === "venue_admin";
  // ===== Amount helpers (manager requirement) =====
  const slotPrice = useMemo(() => {
    // Total Amount should be the actual slot price (before discount)
    const v =
      doc?.slot_price ??
      doc?.originalTotalAmount ??
      doc?.totalAmount ??
      0;
    return Number(v || 0);
  }, [doc?.slot_price, doc?.originalTotalAmount, doc?.totalAmount]);

  const discountAmount = useMemo(() => {
    // Discount amount (prefer booking doc fields; paymentRow is optional)
    const v =
      doc?.discount_amount ??
      doc?.promoDiscountAmount ??
      paymentRow?.promoDiscountAmount ??
      0;
    return Number(v || 0);
  }, [doc?.discount_amount, doc?.promoDiscountAmount, paymentRow?.promoDiscountAmount]);

  const payableToVenue = useMemo(() => {
    // ✅ Payable to Venue = venue settlement from Payment analytics (source of truth)
    // Backend: venueSettlement = slotValue - commissionAmount (gateway fee not subtracted in your rule)
    if (paymentRow?.venueSettlement != null) return Number(paymentRow.venueSettlement || 0);

    // Fallback (only if paymentRow not present): slotPrice - computed commission if percent known
    const pct = Number(paymentRow?.commissionPercent ?? 0);
    if (pct > 0) {
      const commission = Math.round((slotPrice * pct) / 100);
      return Math.max(slotPrice - commission, 0);
    }

    // Last resort: if nothing known, show 0 to avoid lying
    return 0;
  }, [paymentRow?.venueSettlement, paymentRow?.commissionPercent, slotPrice]);

  const userPaid = useMemo(() => {
    // ✅ User actually pays final_amount (after promo) or paidAmount
    const v =
      doc?.paidAmount ??
      doc?.final_amount ??
      Math.max(slotPrice - discountAmount, 0);
    return Number(v || 0);
  }, [doc?.paidAmount, doc?.final_amount, slotPrice, discountAmount]);

  const userDue = useMemo(() => {
    // ✅ Due should come from doc if available, else final payable - paid
    if (doc?.dueAmount != null) return Number(doc.dueAmount || 0);
    const finalPayable = Number(doc?.final_amount ?? Math.max(slotPrice - discountAmount, 0));
    return Math.max(finalPayable - userPaid, 0);
  }, [doc?.dueAmount, doc?.final_amount, slotPrice, discountAmount, userPaid]);



  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getReservation(id);
      setDoc(d);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load reservation");
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fetchPayment = async () => {
      const bookingId = doc?.generatedReservationId || doc?._id;
      if (!bookingId) {
        setPaymentRow(null);
        return;
      }

      setPayLoading(true);
      try {
        // Backend supports q -> matches bookingId via regex
        const res = await listPayments({ page: 1, limit: 10, q: bookingId });

        // pick exact bookingId match first, else fallback to first row
        const items = Array.isArray(res?.items) ? res.items : [];

        const exact =
          items.find((x) => String(x?.bookingId) === String(bookingId)) ||
          items.find((x) => String(x?.bookingId) === String(doc?._id)) ||
          items[0] ||
          null;

        setPaymentRow(exact);
      } catch (e) {
        console.error("fetchPayment error", e);
        setPaymentRow(null);
      } finally {
        setPayLoading(false);
      }
    };

    fetchPayment();
  }, [doc?.generatedReservationId, doc?._id]);


  const schedule = useMemo(
    () => buildScheduleGroups(doc?.reservationDates || []),
    [doc?.reservationDates]
  );

  const primaryDay = useMemo(() => schedule?.[0]?.ymd || "", [schedule]);

  const primarySlotLabel = useMemo(() => {
    const firstDay = schedule?.[0];
    const firstSlot = firstDay?.slots?.[0];
    return firstSlot || "—";
  }, [schedule]);

  const refreshTaken = useCallback(async () => {
    if (!doc?.venueGround || !primaryDay) return setTaken([]);
    setChecking(true);
    try {
      const viewerId = (doc?.userId && (doc.userId._id || doc.userId)) || null;
      const d = await getReservedSlots(doc.venueGround, primaryDay, viewerId);
      setTaken(Array.isArray(d?.reserved) ? d.reserved : []);
    } catch {
      setTaken([]);
    } finally {
      setChecking(false);
    }
  }, [doc?.venueGround, doc?.userId, primaryDay]);

  useEffect(() => {
    if (doc) refreshTaken();
  }, [doc, refreshTaken]);

  const copyId = async () => {
    const txt = doc?.generatedReservationId || doc?._id || "";
    try {
      await navigator.clipboard.writeText(String(txt));
      toast.success("Copied");
    } catch { }
  };

  const handleConfirm = async () => {
    if (
      !window.confirm(
        "Confirm this reservation and mark payment as received (outside the app)?"
      )
    )
      return;
    setBusy(true);
    try {
      await adminConfirmReservation(id);
      toast.success("Reservation confirmed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Confirm failed");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    // open modal instead of window.confirm
    setCancelReason("");
    setCancelOpen(true);
  };

  const performCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please enter a reason for cancellation");
      return;
    }

    setCancelSaving(true);
    setBusy(true);

    try {
      await cancelReservation(id, { reason: cancelReason.trim() }); // ✅ send reason
      toast.success("Reservation cancelled");
      setCancelOpen(false);
      setCancelReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Cancel failed");
    } finally {
      setCancelSaving(false);
      setBusy(false);
    }
  };


  const reservationStatus = String(doc?.reservationStatus || "").toLowerCase();
  const paymentStatus = String(doc?.paymentStatus || "").toLowerCase();
  const rawBookingSource = String(doc?.bookingSource || "")
    .trim()
    .toLowerCase();
  const bookingSource =
    rawBookingSource === "venue_public"
      ? "Venue Public Website"
      : rawBookingSource === "recurring"
        ? "Recurring Booking"
        : rawBookingSource === "admin"
          ? "Admin Booking"
          : rawBookingSource === "user"
            ? "User Booking"
            : rawBookingSource
              ? rawBookingSource
              : doc?.isRecurringHold === true
                ? "Recurring Booking"
                : doc?.isAdminReservation === true
                  ? "Admin Booking"
                  : doc?.isUserReservation === true
                    ? "User Booking"
                    : "—";

  const canConfirm =
    reservationStatus === "pending" && paymentStatus !== "completed";
  const canCancel =
    reservationStatus === "pending" || reservationStatus === "confirmed";

  const bookingStatusPillTone =
    reservationStatus === "confirmed"
      ? "success"
      : reservationStatus === "cancelled" || reservationStatus === "expired"
        ? "danger"
        : "warn";

  const paymentStatusPillTone =
    paymentStatus === "completed" ? "success" : "warn";

  return (
    <div className="sa-bd-wrap">
      {/* HEADER */}
      <div className="sa-bd-header">
        <div>
          <h1 className="sa-bd-title">Booking Details</h1>
          <div className="sa-bd-sub">
            Created on <strong>{fmtCreatedOn(doc?.createdAt)}</strong>
          </div>
        </div>

        <div className="sa-bd-header-actions">
          <button className="sa-btn-outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="lucide" /> Back
          </button>
        </div>
      </div>

      {/* LOADING / EMPTY */}
      {loading ? (
        <div className="sa-card">
          <div className="sa-placeholder" style={{ height: 160 }}>
            Loading…
          </div>
        </div>
      ) : !doc ? (
        <div className="sa-card">
          <div className="sa-empty">Reservation not found.</div>
        </div>
      ) : (
        <>
          {/* TOP INFO (4 boxes) */}
          <div className="sa-bd-toprow">
            <div className="sa-bd-info">
              <div className="sa-bd-label">Booking ID</div>
              <div className="sa-bd-value">
                <Hash className="lucide muted" />
                <span className="sa-bd-strong">
                  {doc.generatedReservationId || doc._id}
                </span>
                <button
                  type="button"
                  className="sa-bd-copy"
                  onClick={copyId}
                  title="Copy ID"
                >
                  <Copy className="lucide xs" /> Copy ID
                </button>
              </div>
            </div>

            <div className="sa-bd-info">
              <div className="sa-bd-label">Player</div>
              <div className="sa-bd-value">
                <UserRound className="lucide muted" />
                <span className="sa-bd-strong">
                  {doc.userName || doc?.userId?.fullName || "—"}
                </span>
              </div>
              <div className="sa-bd-muted">
                <Phone className="lucide xs" />{" "}
                {doc.contactNumber || doc?.userId?.mobileNumber || "—"}
              </div>
            </div>

            <div className="sa-bd-info">
              <div className="sa-bd-label">Venue</div>
              <div className="sa-bd-value">
                <MapPinned className="lucide muted" />
                <span className="sa-bd-strong">{doc.venueName || "—"}</span>
              </div>
              <div className="sa-bd-muted">{doc.groundName || "—"}</div>
            </div>

            <div className="sa-bd-info">
              <div className="sa-bd-label">Amount</div>
              <div className="sa-bd-value">
                <IndianRupee className="lucide muted" />
                <span className="sa-bd-strong">{fmtMoney(slotPrice)}</span>
              </div>

              <div className="sa-bd-muted">
                Paid {fmtMoney(userPaid)} · Due {fmtMoney(userDue)}
              </div>
            </div>
          </div>

          {/* MAIN CONTENT 2-COL */}
          <div className="sa-bd-main">
            {/* LEFT */}
            <div className="sa-bd-col">
              {/* STATUS & ACTIONS */}
              <div className="sa-card">
                <div className="sa-bd-cardhd">
                  <ShieldCheck className="lucide" />
                  <div>
                    <div className="sa-bd-cardtitle">Status & Actions</div>
                    <div className="sa-bd-cardsub">
                      {reservationStatus === "confirmed"
                        ? "This booking is confirmed."
                        : reservationStatus === "cancelled"
                          ? "This booking is cancelled."
                          : reservationStatus === "expired"
                            ? "This booking is expired."
                            : "This booking is pending."}
                    </div>
                  </div>
                </div>

                <div className="sa-bd-pillrow">
                  <span className={chip(bookingStatusPillTone)}>
                    Booking Status:{" "}
                    <strong style={{ textTransform: "capitalize" }}>
                      {reservationStatus || "—"}
                    </strong>
                  </span>
                  <span className={chip(paymentStatusPillTone)}>
                    Payment Status:{" "}
                    <strong style={{ textTransform: "capitalize" }}>
                      {paymentStatus || "—"}
                    </strong>
                  </span>
                </div>

                <div className="sa-bd-actions">
                  <button
                    className="sa-btn-danger"
                    onClick={handleCancel}
                    disabled={!canCancel || busy}
                  >
                    <XCircle className="lucide" /> Cancel Booking
                  </button>

                  <button
                    className="sa-btn-outline"
                    onClick={refreshTaken}
                    disabled={checking}
                  >
                    <RefreshCw className="lucide" />{" "}
                    {checking ? "Checking…" : "Refresh Availability"}
                  </button>

                  {canConfirm && (
                    <button
                      className="sa-btn sa-btn--primary"
                      onClick={handleConfirm}
                      disabled={busy}
                    >
                      <CheckCircle2 className="lucide" /> Mark as Paid
                    </button>
                  )}
                </div>

                <div className="sa-bd-muted" style={{ marginTop: 10 }}>
                  Cancelling this booking will cancel all scheduled slots under this booking.
                </div>

                <div className="sa-bd-kvlist" style={{ marginTop: 14 }}>
                  <div className="sa-bd-kv">
                    <div className="sa-bd-k">Booking Source</div>
                    <div className="sa-bd-v">{bookingSource}</div>
                  </div>

                  <div className="sa-bd-kv">
                    <div className="sa-bd-k">Razorpay Order</div>
                    <div className="sa-bd-v">{doc.razorpayOrderId || "—"}</div>
                  </div>
                </div>
                {/* ✅ Show cancellation reason after cancel */}
                {/* ✅ Show cancellation reason after cancel (beautiful UI) */}
                {reservationStatus === "cancelled" && (
                  <div className="sa-cancel-reason">
                    <XCircle className="lucide sa-cancel-reason__icon" />
                    <div className="sa-cancel-reason__content">
                      <div className="sa-cancel-reason__title">Cancellation reason</div>
                      <div className="sa-cancel-reason__text">
                        {doc?.cancellationReason?.trim() ? doc.cancellationReason : "—"}
                      </div>
                    </div>
                  </div>
                )}


              </div>

              {/* SCHEDULE */}
              <div className="sa-card">
                <div className="sa-bd-cardhd">
                  <CalendarDays className="lucide" />
                  <div>
                    <div className="sa-bd-cardtitle">Schedule</div>
                    <div className="sa-bd-cardsub">
                      All slots are part of the same booking.
                    </div>
                  </div>
                </div>

                {schedule?.length ? (
                  <div className="sa-bd-schedule">
                    {schedule.map((day) => (
                      <div key={day.ymd} className="sa-bd-day">
                        <div className="sa-bd-daydate">{day.dateLabel}</div>
                        {day.slots?.length ? (
                          <div className="sa-bd-slots">
                            {day.slots.map((slot, idx) => (
                              <span key={idx} className="sa-bd-slot">
                                {slot}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="sa-bd-slot">—</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="sa-bd-muted">No schedule found.</div>
                )}
              </div>

              {/* PAYMENT SUMMARY */}
              {/* PAYMENT SUMMARY */}
              <div className="sa-card">
                <div className="sa-bd-cardhd">
                  <Clock3 className="lucide" />
                  <div>
                    <div className="sa-bd-cardtitle">Payment Summary</div>
                    <div className="sa-bd-cardsub">Clean breakdown</div>
                  </div>
                </div>

                <div className="sa-bd-kvlist">
                  {payLoading ? (
                    <div className="sa-bd-muted">Loading payment summary…</div>
                  ) : (
                    <>
                      {/* Total */}
                      <div className="sa-bd-kv">
                        <div className="sa-bd-k">Total Amount</div>
                        <div className="sa-bd-v">
                          {fmtMoney(slotPrice)}
                        </div>
                      </div>

                      {/* Promo (single line: code + discount) */}
                      <div className="sa-bd-kv">
                        <div className="sa-bd-k">Promo</div>
                        <div className="sa-bd-v">
                          {(() => {
                            const code =
                              (doc?.promo_code || doc?.promoCode || paymentRow?.promoCode || "").trim();

                            const disc = Number(
                              doc?.discount_amount ??
                              doc?.promoDiscountAmount ??
                              paymentRow?.promoDiscountAmount ??
                              0
                            );

                            if (!code && !disc) return "—";
                            if (code && disc > 0) return `${code} (− ${fmtMoney(disc)})`;
                            if (code) return code;
                            return `− ${fmtMoney(disc)}`;
                          })()}
                        </div>
                      </div>

                      {/* Gateway fee (hide for venue admin) */}
                      {/* {!isVenueAdmin && (
                              <div className="sa-bd-kv">
                                <div className="sa-bd-k">Gateway Fee</div>
                                <div className="sa-bd-v">{fmtMoney(paymentRow?.gatewayFee ?? 0)}</div>
                              </div>
                            )} */}

                      <div className="sa-bd-kv">
                        <div className="sa-bd-k">User Paid</div>
                        <div className="sa-bd-v">{fmtMoney(userPaid)}</div>
                      </div>


                      {/* Platform fee / commission */}
                      <div className="sa-bd-kv">
                        <div className="sa-bd-k">Platform Fee</div>
                        <div className="sa-bd-v">{fmtMoney(paymentRow?.commissionAmount ?? 0)}</div>
                      </div>

                      {/* Venue settlement */}
                      <div className="sa-bd-kv">
                        <div className="sa-bd-k">Payable to Venue</div>
                        <div className="sa-bd-v">{fmtMoney(payableToVenue)}</div>
                      </div>

                      {/* tiny note when no payment row exists yet */}
                      {!paymentRow && (
                        <div className="sa-bd-muted" style={{ marginTop: 10 }}>
                          Payment record not generated yet — showing booking-side values where possible.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>


            </div>

            {/* RIGHT */}
            <div className="sa-bd-col">
              {/* AVAILABILITY */}
              <div className="sa-card">
                <div className="sa-bd-cardhd">
                  <CalendarDays className="lucide" />
                  <div>
                    <div className="sa-bd-cardtitle">Day Availability</div>
                    <div className="sa-bd-cardsub">
                      Taken slots on <strong>{primaryDay || "—"}</strong>:
                    </div>
                  </div>
                </div>

                <div className="sa-bd-pillwrap">
                  {(taken || []).length ? (
                    taken.map((s, i) => (
                      <span key={i} className={`${chip("danger")} sa-bd-pill`}>
                        {fmtTimeRange(s)}
                      </span>
                    ))
                  ) : (
                    <div className="sa-bd-muted">No conflicts</div>
                  )}
                </div>

                <div className="sa-bd-muted" style={{ marginTop: 10 }}>
                  Your slot:
                </div>
                <div className="sa-bd-pillrow" style={{ marginTop: 6 }}>
                  <span className={`${chip("success")} sa-bd-pill`}>
                    {primarySlotLabel}
                  </span>
                </div>
              </div>

              {/* AUDIT */}
              <div className="sa-card">
                <div className="sa-bd-cardhd">
                  <Clock3 className="lucide" />
                  <div>
                    <div className="sa-bd-cardtitle">Audit Timeline</div>
                    <div className="sa-bd-cardsub">System events</div>
                  </div>
                </div>

                <div className="sa-bd-kvlist">
                  <div className="sa-bd-kv">
                    <div className="sa-bd-k">
                      <strong>Created</strong>
                    </div>
                    <div className="sa-bd-v">{fmtCreatedOn(doc.createdAt)}</div>
                  </div>

                  <div className="sa-bd-kv">
                    <div className="sa-bd-k">
                      <strong>Updated</strong>
                    </div>
                    <div className="sa-bd-v">{fmtCreatedOn(doc.updatedAt)}</div>
                  </div>

                  <div className="sa-bd-kv">
                    <div className="sa-bd-k">
                      <strong>Payment Deadline</strong>
                    </div>
                    <div className="sa-bd-v">
                      {fmtCreatedOn(doc.paymentDeadline)}
                    </div>
                  </div>

                  <div className="sa-bd-kv">
                    <div className="sa-bd-k">
                      <strong>Short-hold Expires</strong>
                    </div>
                    <div className="sa-bd-v">
                      {fmtCreatedOn(doc.reservationExpiresAt)}
                    </div>
                  </div>

                  <div className="sa-bd-kv">
                    <div className="sa-bd-k">
                      <strong>Released At</strong>
                    </div>
                    <div className="sa-bd-v">{fmtCreatedOn(doc.releasedAt)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="sa-bd-footer">
            <button className="sa-btn-outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="lucide" /> Back
            </button>

            <div className="sa-bd-footer-r">
              <button className="sa-btn-outline" onClick={copyId}>
                <Copy className="lucide" /> Copy ID
              </button>

              <button
                className="sa-btn-danger"
                disabled={!canCancel || busy}
                onClick={handleCancel}
              >
                <XCircle className="lucide" /> Cancel Booking
              </button>
            </div>
          </div>
        </>
      )}
      {/* ✅ Cancel modal (same UX as list page) */}
      {cancelOpen && (
        <div className="sa-modal">
          <div
            className="sa-modal-backdrop"
            onClick={() => !cancelSaving && setCancelOpen(false)}
          />
          <div className="sa-modal-card" style={{ maxWidth: 560 }}>
            <div className="sa-modal-hd">
              <h3>Cancel Booking</h3>
              <button
                className="sa-x"
                onClick={() => !cancelSaving && setCancelOpen(false)}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "12px 6px" }}>
              <div className="sa-warn">
                This will cancel the booking and notify the user with your reason.
              </div>

              <div className="sa-field" style={{ marginTop: 10 }}>
                <label>
                  Reason for cancellation <span style={{ color: "red" }}>*</span>
                </label>
                <textarea
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., Ground maintenance / double booking / weather issues"
                  style={{
                    width: "100%",
                    marginTop: 4,
                    resize: "vertical",
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                  disabled={cancelSaving}
                />
              </div>
            </div>

            <div className="sa-modal-actions">
              <button
                className="sa-btn-outline"
                onClick={() => setCancelOpen(false)}
                disabled={cancelSaving}
              >
                Keep Booking
              </button>

              <button
                className="sa-btn-danger"
                onClick={performCancel}
                disabled={cancelSaving}
              >
                {cancelSaving ? "Cancelling…" : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>

  );
}
